import type { TodoType } from '@goodchat/shared/constants';

/**
 * Shell-level presentation logic for the focused Todos page, kept pure so it
 * can be tested without a DOM.
 *
 * Row-level formatting (age, due dates, snooze wording) is NOT here — it lives
 * in `components/todos/todo-row.tsx`, the single row implementation shared by
 * the page and the per-room pane. This file covers only what the page's shell
 * owns: the multi-select type chips and the add-task audience wording.
 */

// ============================================================
// Type filter
// ============================================================

/** Filter chips name a category, so they read as plurals. */
export const TODO_TYPE_LABELS: Record<TodoType, string> = {
  ack: 'Announcements',
  reply_later: 'Reply later',
  poll: 'Polls',
  task: 'Tasks',
};

/**
 * Which `counts` field each chip reads.
 *
 * Verified against the running API: `counts` respond to orgId/areaId/status but
 * IGNORE the `type` filter — `?type=task` still returns poll counts. That is
 * exactly right for a chip, which should say "how many of these exist", not
 * "how many are currently showing", so the numbers hold still while you click.
 */
export const TODO_TYPE_COUNT_KEY: Record<TodoType, 'acks' | 'replyLater' | 'polls' | 'tasks'> = {
  ack: 'acks',
  reply_later: 'replyLater',
  poll: 'polls',
  task: 'tasks',
};

export const ALL_TODO_TYPES: readonly TodoType[] = ['ack', 'reply_later', 'poll', 'task'];

/**
 * Chip selection → the `type` query param.
 *
 * Both "nothing selected" and "everything selected" mean no filtering, and both
 * must produce `undefined` rather than a four-item list: the store keys its
 * cached views off this value, so returning a list for the all-selected case
 * would open a second cache entry holding byte-identical data.
 */
export function resolveTypeFilter(selected: ReadonlySet<TodoType>): readonly TodoType[] | undefined {
  if (selected.size === 0 || selected.size >= ALL_TODO_TYPES.length) return undefined;
  return ALL_TODO_TYPES.filter((t) => selected.has(t));
}

/** Parse `?type=` back into a chip selection, ignoring anything unrecognised. */
export function parseTypeParam(raw: string | undefined): Set<TodoType> {
  if (!raw) return new Set();
  const wanted = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is TodoType => (ALL_TODO_TYPES as readonly string[]).includes(s));
  const set = new Set(wanted);
  // All four is the same as none; normalize so the URL and the chips agree.
  return set.size >= ALL_TODO_TYPES.length ? new Set() : set;
}

// ============================================================
// Task audience
// ============================================================

export interface TaskAudienceInput {
  /** Room the task will live in, already `#`-prefixed; null = org-level. */
  channelName: string | null;
  isInternal: boolean;
}

/**
 * Who will actually see this task, in words.
 *
 * Checked against the running API rather than inferred, because the honest
 * answer is not the one the `isInternal` flag suggests:
 *
 *   - org-level (no room), unassigned → ONLY the creator. A second member of
 *     the same org does not see it, whatever `isInternal` says. "Only your team"
 *     there would be a plain lie.
 *   - room + internal  → room members except external guests.
 *   - room + everyone  → every member of the room, guests included.
 *
 * Tasks default to internal for the same reason comments do: you cannot leak
 * internal chatter to a vendor, but you can write something meant for them that
 * they never see. So the audience is stated, never implied.
 */
export function describeTaskAudience({ channelName, isInternal }: TaskAudienceInput): string {
  if (!channelName) return 'Only you will see this';
  return isInternal
    ? `Only your team in ${channelName} will see this — guests are excluded`
    : `Everyone in ${channelName} will see this, including guests`;
}

/** Short pill label, mirroring the comment composer's Internal / Everyone. */
export function taskAudienceLabel(channelName: string | null, isInternal: boolean): string {
  if (!channelName) return 'Private';
  return isInternal ? 'Internal' : 'Everyone';
}

// ============================================================
// Grouping
// ============================================================

/**
 * Buckets for the list's section headers.
 *
 * A flat queue gives no sense of shape — you cannot tell "four things, all
 * from this morning" from "four things, one of them three weeks old" without
 * reading every age stamp. Bucketing by how long something has been waiting
 * makes the shape of your backlog the first thing you see, which is the whole
 * argument for sorting oldest-first in the first place.
 *
 * Bucketed on AGE, not due date: only tasks can have a due date, so due-date
 * buckets would leave three of the four sources permanently in "no date".
 */
export type TodoBucket = 'older' | 'week' | 'recent' | 'today';

export const TODO_BUCKET_LABELS: Record<TodoBucket, string> = {
  older: 'Waiting over a month',
  week: 'Waiting over a week',
  recent: 'Earlier this week',
  today: 'Today',
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function bucketFor(iso: string, now: number = Date.now()): TodoBucket {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'today';
  const age = now - then;
  if (age >= 30 * DAY_MS) return 'older';
  if (age >= 7 * DAY_MS) return 'week';
  if (age >= DAY_MS) return 'recent';
  return 'today';
}

export interface TodoGroup<T> {
  bucket: TodoBucket;
  label: string;
  items: T[];
}

/**
 * Split an already-sorted list into buckets, preserving order.
 *
 * Deliberately does NOT reorder: the server's oldest-first sequence is the
 * product decision, and a client that re-sorted would quietly contradict the
 * line on screen telling the user what the order is.
 */
export function groupByAge<T extends { sortAt: string }>(
  items: readonly T[],
  now: number = Date.now(),
): TodoGroup<T>[] {
  const groups: TodoGroup<T>[] = [];
  for (const item of items) {
    const bucket = bucketFor(item.sortAt, now);
    const last = groups[groups.length - 1];
    if (last && last.bucket === bucket) last.items.push(item);
    else groups.push({ bucket, label: TODO_BUCKET_LABELS[bucket], items: [item] });
  }
  return groups;
}
